import { useCallback, useEffect, useState } from 'react';
import { applyAppUpdate, hasNewVersion } from '@/lib/appVersion';

/** 新バージョンの確認間隔（表示中のみ）。 */
const CHECK_INTERVAL = 5 * 60 * 1000;

/** 起動直後は描画を優先するため、最初の確認を少し遅らせる。 */
const INITIAL_DELAY = 8000;

/**
 * 新バージョンがデプロイされたかどうかを監視するフック（全画面共通）。
 *
 * - 起動時・一定間隔・タブ復帰／フォーカス時に確認する
 * - 画面が見えていないとき（バックグラウンド）は確認しない
 * - 新バージョンを見つけると updateAvailable を true にする
 * - applyUpdate で確実なリロードを実行する
 */
export function useAppUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [applying, setApplying] = useState(false);

  const check = useCallback(async () => {
    const found = await hasNewVersion();
    if (found) setUpdateAvailable(true);
  }, []);

  useEffect(() => {
    if (__IS_PREVIEW__) return undefined;

    const initial = window.setTimeout(check, INITIAL_DELAY);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, CHECK_INTERVAL);

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      window.clearTimeout(initial);
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [check]);

  const applyUpdate = useCallback(async () => {
    setApplying(true);
    await applyAppUpdate();
  }, []);

  return { updateAvailable, applying, applyUpdate };
}